import React, { useState, useEffect } from 'react';
import styles from './index.module.scss';

interface RecommendationItem {
  id: string;
  title?: string;
  reason?: string;
  image?: string;
  url?: string;
}

interface RecommendationsWidgetProps {
  id?: number;
  heading?: string;
  count?: number;
  // Supplied by the GetRecommendationsWidget handler (server-side). All optional so
  // the component still works with sensible fallbacks if wired without a handler.
  apiBase?: string;
  userStorageKey?: string;
  modelSlug?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const w = (typeof window !== 'undefined' ? window : {}) as any;

// Same signals inchcape-cdp.js reads (explicit hook, then Ketch "analytics" category).
function hasConsent(): boolean {
  try {
    const fn = w.CdpRecsConsent || w.SubaruRecsConsent; // brand-neutral, legacy fallback
    if (typeof fn === 'function') return fn() === true;
  } catch {
    /* ignore */
  }
  try {
    if (w.ketchConsent && typeof w.ketchConsent.analytics !== 'undefined')
      return w.ketchConsent.analytics === true;
  } catch {
    /* ignore */
  }
  return false;
}

// The visitor fingerprint the CDP exposes (== cdp_events.user_id). Brand-neutral name
// (window.CdpUserId) with a legacy Subaru* fallback during the transition.
function cdpUserId(): string | null {
  return w.CdpUserId || w.SubaruCdpUserId || null;
}

function pushCDP(payload: unknown): void {
  try {
    if (typeof w.pushDigitalData === 'function') w.pushDigitalData(payload);
  } catch {
    /* analytics must never break the widget */
  }
}

// The CDP session id inchcape-cdp.js uses (sessionStorage `wa_session_id`) == the
// cdp_events.session_id; reuse it so Personalize groups live events into the same session.
function cdpSessionId(): string {
  try {
    let sid = window.sessionStorage.getItem('wa_session_id');
    if (!sid) {
      sid =
        w.crypto && w.crypto.randomUUID
          ? w.crypto.randomUUID()
          : `sess-${Math.random().toString(36).slice(2)}`;
      window.sessionStorage.setItem('wa_session_id', sid);
    }
    return sid;
  } catch {
    return 'na';
  }
}

export const RecommendationsWidget = (
  props: RecommendationsWidgetProps
): React.ReactElement => {
  const heading = props.heading || 'You might also like';
  const count = props.count || 4;
  const apiBase = (
    props.apiBase ||
    w.RecommendationsApiBase ||
    'https://ai-api-staging.inchcape-web.au'
  ).replace(/\/$/, '');
  const modelSlug =
    props.modelSlug ||
    (typeof window !== 'undefined'
      ? window.location.pathname.split('/').filter(Boolean)[0] || ''
      : '');

  const [items, setItems] = useState<RecommendationItem[]>([]);
  const [source, setSource] = useState<string>('');
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false); // re-fetch (reactive) in progress

  // Client-only: resolve identity + consent, fetch, and stay reactive to live signals.
  // api.ai serves CORS *, so the browser calls it directly. Fails quiet.
  useEffect(() => {
    let cancelled = false;
    const ctx = modelSlug
      ? `&context_item=${encodeURIComponent(modelSlug)}`
      : '';

    // Whichever fetch resolves last wins setItems; guard so a late /popular response
    // can't clobber personalised results that already landed.
    let personalizedShown = false;
    function fetchUrl(url: string, isPersonalized: boolean) {
      fetch(url, {
        credentials: 'omit',
        headers: { Accept: 'application/json' },
      })
        .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
        .then((data) => {
          if (cancelled) return;
          if (!isPersonalized && personalizedShown) return;
          if (isPersonalized) personalizedShown = true;
          setItems((data && data.items) || []);
          setSource((data && data.source) || '');
          setReady(true);
        })
        .catch(() => {
          if (!cancelled && !personalizedShown) setReady(true);
        });
    }

    // Record a live interaction to Personalize (POST /recommendations/events).
    // Best-effort; the next for-user fetch reflects it (real-time session personalization).
    function sendEvent(uid: string, itemId: string | null, eventType: string) {
      return fetch(`${apiBase}/recommendations/events`, {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: uid,
          session_id: cdpSessionId(),
          event_type: eventType,
          item_id: itemId || undefined,
        }),
      }).catch(() => {
        /* best-effort; a live event must never break the page */
      });
    }

    // Re-fetch personalised recs with the loader shown (used by the reactive signal).
    // `related` routes to the co-occurrence "related to what you searched" strip.
    function refetchPersonalized(
      uid: string,
      contextItem: string,
      related?: boolean
    ) {
      if (cancelled) return;
      setLoading(true);
      const c = contextItem
        ? `&context_item=${encodeURIComponent(contextItem)}`
        : ctx;
      const rel = related ? '&related=1' : '';
      fetch(
        `${apiBase}/recommendations/for-user/${encodeURIComponent(
          uid
        )}?num=${encodeURIComponent(String(count))}&consent=true${c}${rel}`,
        { credentials: 'omit', headers: { Accept: 'application/json' } }
      )
        .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
        .then((data) => {
          if (cancelled) return;
          personalizedShown = true;
          setItems((data && data.items) || []);
          setSource((data && data.source) || '');
          setReady(true);
        })
        .catch(() => {
          /* keep the previous cards on error */
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }

    // true once a for-user (personalised) fetch has been kicked off.
    let personalized = false;
    let safetyTimer: ReturnType<typeof setTimeout> | null = null;

    function popular() {
      if (cancelled || personalized) return;
      fetchUrl(
        `${apiBase}/recommendations/popular?num=${encodeURIComponent(
          String(count)
        )}${ctx}`,
        false
      );
    }

    // Ketch's boot.js is deferred and its "consent" event can fire seconds after mount
    // (esp. on a cold/incognito load, where analytics is granted by default). Ketch
    // replays the current consent to new subscribers, so read analytics from the event
    // itself (authoritative) rather than racing a short timer against globals.
    let ketchAnalytics: boolean | null = null; // null = not yet resolved
    function consentGranted(): boolean {
      if (ketchAnalytics !== null) return ketchAnalytics;
      return hasConsent();
    }

    // Personalise as soon as BOTH consent and the fingerprint (window.SubaruCdpUserId,
    // == cdp_events.user_id) are known. Safe to call repeatedly; upgrades a visitor who
    // was shown /popular while consent/fingerprint were still resolving.
    function tryPersonalize(): boolean {
      if (personalized || cancelled) return true;
      if (!consentGranted()) return false;
      const uid = cdpUserId();
      if (!uid) return false;
      personalized = true;
      if (safetyTimer) {
        clearTimeout(safetyTimer);
        safetyTimer = null;
      }
      // Record the current model view so recs on the next page / next signal reflect
      // the growing session (real-time). Serving-only — never a training source.
      if (modelSlug) sendEvent(uid, modelSlug, 'view');
      fetchUrl(
        `${apiBase}/recommendations/for-user/${encodeURIComponent(
          uid
        )}?num=${encodeURIComponent(String(count))}&consent=true${ctx}`,
        true
      );
      return true;
    }

    // Reactive: the page (e.g. the search box) dispatches a window `CdpRecsSignal`
    // CustomEvent with detail {itemId?, eventType?, context?} when the visitor does
    // something the recs should react to. Record it, then re-fetch so the strip updates.
    const onSignal = (e: Event) => {
      const uid = cdpUserId();
      if (!consentGranted() || !uid) return; // only react when we can personalise
      const detail = ((e as CustomEvent).detail || {}) as {
        itemId?: string;
        item?: string;
        eventType?: string;
        context?: string;
        related?: boolean;
      };
      const itemId = detail.itemId || detail.item || null;
      const eventType = detail.eventType || 'view';
      const contextItem = detail.context || itemId || modelSlug || '';
      sendEvent(uid, itemId, eventType).then(() =>
        refetchPersonalized(uid, contextItem, detail.related)
      );
    };
    window.addEventListener('CdpRecsSignal', onSignal);

    // Initial load: re-evaluate on the two async signals (Ketch consent + fingerprint).
    const onConsent = (c: { purposes?: Record<string, boolean> }) => {
      ketchAnalytics = c?.purposes?.analytics === true;
      if (!tryPersonalize() && ketchAnalytics === false) popular();
    };
    const onFingerprint = () => {
      tryPersonalize();
    };
    try {
      if (typeof w.ketch === 'function') w.ketch('on', 'consent', onConsent);
    } catch {
      /* ignore */
    }
    window.addEventListener('CdpUserIdReady', onFingerprint);
    window.addEventListener('SubaruCdpUserIdReady', onFingerprint); // legacy alias

    if (!tryPersonalize()) {
      safetyTimer = setTimeout(popular, 6000);
    }

    return () => {
      cancelled = true;
      if (safetyTimer) clearTimeout(safetyTimer);
      window.removeEventListener('CdpUserIdReady', onFingerprint);
      window.removeEventListener('SubaruCdpUserIdReady', onFingerprint);
      window.removeEventListener('CdpRecsSignal', onSignal);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Emit one CDP impression event whenever a fresh set of cards is shown.
  useEffect(() => {
    if (ready && !loading && items.length) {
      pushCDP({
        event: '_recommendationImpression',
        recommendation: {
          source,
          context: modelSlug || null,
          items: items.map((it) => it.id),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, loading, items, source]);

  // Stable root for hydration; content appears once loaded (or while re-fetching).
  return (
    <div
      className={styles.recommendations}
      id={props.id ? `cid-${props.id}` : undefined}
    >
      {(ready && items.length > 0) || loading ? (
        <div className={styles.container}>
          <h2 className={styles.heading}>{heading}</h2>
          <div
            className={
              loading ? `${styles.grid} ${styles.gridLoading}` : styles.grid
            }
            aria-busy={loading || undefined}
          >
            {items.map((it, i) => (
              <a
                key={it.id || i}
                className={styles.card}
                href={it.url || '#'}
                data-rec-id={it.id || ''}
                onClick={() =>
                  pushCDP({
                    event: '_recommendationClick',
                    recommendation: {
                      id: it.id,
                      position: i + 1,
                      source,
                      context: modelSlug || null,
                    },
                  })
                }
              >
                {it.image ? (
                  <img
                    className={styles.img}
                    src={it.image}
                    alt={it.title || ''}
                    loading="lazy"
                  />
                ) : (
                  <div className={styles.img} />
                )}
                <div className={styles.body}>
                  <p className={styles.title}>{it.title || it.id}</p>
                  {it.reason ? (
                    <p className={styles.reason}>{it.reason}</p>
                  ) : null}
                </div>
              </a>
            ))}
          </div>
          {loading ? (
            <p className={styles.loader} role="status" aria-live="polite">
              Updating recommendations…
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
